import { Link } from 'react-router-dom';
import { line, resolvedStatusPill, t, trackingHref, type PackageRow, type SalesOrderShipmentRow } from '@bms2/shared';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { Money } from '../../components/Money';
import { useStatusResolver } from '../../shell/StatusResolver';
import { PackageModal } from './PackageModal';
import { PACKAGE_ROUTE } from './PackagePage';
import { ShipPackagesModal, shippedNotice } from './ShipPackagesModal';
import { orderPackagesPath, useApi } from './api';
import { ViewPdfLink, packingSlipPath } from './ViewPdfLink';
import styles from './DocumentPage.module.css';

/** The order's Packages and Shipments, read together off the one route (decision 1730). */
export type OrderPackagesRead = ReturnType<typeof useOrderPackages>;

export function useOrderPackages(salesOrderId: string | null, apiBaseUrl: string) {
  return useApi<{ packages: PackageRow[]; shipments: SalesOrderShipmentRow[] }>(salesOrderId ? orderPackagesPath(salesOrderId) : null, apiBaseUrl);
}

/**
 * 🔒 The order's Packages block and Shipments block (decisions 1730, 1731), the
 * two lists the owner's Zoho order draws beneath its lines: each package with
 * its date, its quantity and where it stands, opening its own page, and each
 * shipment with its carrier, its tracking link, its charge and its Packing Slip.
 */
export function OrderPackagesBlocks({ read, currencyCode, apiBaseUrl = '' }: { read: OrderPackagesRead; currencyCode: string; apiBaseUrl?: string }) {
  const resolveStatus = useStatusResolver();
  if (read.state.kind !== 'ready') return null;
  const { packages, shipments } = read.state.data;
  return (
    <>
      <section className={styles.block} data-sales-order-packages={packages.length}>
        <h2 className={styles.heading}>{t('salesOrder.packages.title')}</h2>
        {packages.length === 0 ? <p className={styles.muted}>{t('salesOrder.packages.none')}</p> : null}
        <DetailList look="separator">
          {packages.map((pkg) => (
            <DetailRow
              key={pkg.id}
              data-order-package={pkg.id}
              data-package-status={pkg.status}
              title={<Link to={`${PACKAGE_ROUTE}/${pkg.id}`}>{pkg.packageNumber}</Link>}
              detail={line(pkg.packageDate, '·', String(pkg.totalQuantity), t('shipment.window.units'))}
              columns={[{ value: resolvedStatusPill(resolveStatus, 'PackageStatus', pkg.status).text }, { value: pkg.shipmentNumber ?? '' }]}
            />
          ))}
        </DetailList>
      </section>
      <section className={styles.block} data-sales-order-shipments={shipments.length}>
        <h2 className={styles.heading}>{t('salesOrder.shipments.title')}</h2>
        {shipments.length === 0 ? <p className={styles.muted}>{t('salesOrder.shipments.none')}</p> : null}
        <DetailList look="separator">
          {shipments.map((row) => {
            const href = trackingHref(row.trackingUrl);
            return (
              <DetailRow
                key={row.id}
                data-order-shipment={row.id}
                data-shipment-status={row.status}
                title={row.shipmentNumber}
                detail={[
                  line(row.shippedDate ?? '', row.carrier ? '·' : '', row.carrier ?? '', row.originLabel ? '·' : '', row.originLabel ?? ''),
                  /* 🔒 The vendor's shipment straight to the customer, named by its purchase order (decision 1930). */
                  row.dropShipOrderNumber ? <span key="drop-ship" data-order-shipment-drop-ship={row.dropShipOrderNumber}>{line(t('salesOrder.shipments.dropShipped'), '·', row.dropShipOrderNumber)}</span> : null,
                ]}
                columns={[
                  { value: resolvedStatusPill(resolveStatus, 'ShipmentStatus', row.status).text },
                  { value: href ? <a href={href} target="_blank" rel="noreferrer" data-shipment-tracking={row.trackingNumber ?? ''}>{row.trackingNumber ?? href}</a> : (row.trackingNumber ?? '') },
                ]}
                actions={<ViewPdfLink path={packingSlipPath(row.id)} apiBaseUrl={apiBaseUrl} documentType="Shipment" label={t('salesOrder.shipments.slip')} />}
              >
                <span className={styles.rowAmount}>{row.shippingChargesCents === null ? '' : <Money cents={row.shippingChargesCents} currencyCode={currencyCode} />}</span>
              </DetailRow>
            );
          })}
        </DetailList>
      </section>
    </>
  );
}

export type OrderPackageWindow = 'package' | 'ship' | null;

/**
 * 🔒 The windows Create package and Create shipment open (decisions 1730, 1731),
 * held open by the order's page so its footer and its What's next banner open
 * the same ones (decision 1732). Create shipment on an order with nothing packed
 * opens the refusal, whose New package opens the package window in its place.
 */
export function OrderPackageWindows({ salesOrderId, open, onOpen, apiBaseUrl = '', onChanged, onNotice }: {
  salesOrderId: string;
  open: OrderPackageWindow;
  onOpen: (next: OrderPackageWindow) => void;
  apiBaseUrl?: string;
  onChanged: () => void;
  onNotice: (message: string) => void;
}) {
  if (open === 'package') {
    return (
      <PackageModal
        salesOrderId={salesOrderId}
        apiBaseUrl={apiBaseUrl}
        onCancel={() => onOpen(null)}
        onSaved={(saved) => {
          onOpen(null);
          onNotice(line(t('package.saved'), saved.packageNumber));
          onChanged();
        }}
      />
    );
  }
  if (open === 'ship') {
    return (
      <ShipPackagesModal
        salesOrderId={salesOrderId}
        apiBaseUrl={apiBaseUrl}
        onCancel={() => onOpen(null)}
        onShipped={(answer) => {
          onOpen(null);
          onNotice(shippedNotice(answer));
          onChanged();
        }}
        onNewPackage={() => onOpen('package')}
      />
    );
  }
  return null;
}
